"use client";

import React, { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "./context/LanguageContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import Link from "next/link";

import "swiper/css";
import "swiper/css/pagination";

// ---------- TYPES ----------
type Lang = "en" | "fr" | "ar";

interface Specialite {
  id: number;
  title: string;
  Slug: string;
  description: string;
  cover?: string;
}

export default function SpecialtiesPage() {
  const [specialites, setSpecialites] = useState<Specialite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { language } = useLanguage() as { language: Lang };
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // ---------- TRANSLATIONS ----------
  const translations: Record<
    Lang,
    { title: string; subtitle: string; buttoon: string }
  > = {
    en: {
      title: "Our Specialties",
      subtitle:
        "An overview of our products that help you achieve perfect results",
      buttoon: "See more specialties → ",
    },
    fr: {
      title: "Nos Spécialités",
      subtitle:
        "Un aperçu de nos produits pour vous aider à obtenir des résultats parfaits",
      buttoon: "Voir plus Specialties → ",
    },
    ar: {
      title: "تخصصاتنا",
      subtitle:
        "نظرة عامة على منتجاتنا التي تساعدك على الحصول على أفضل النتائج",
      buttoon: "عرض المزيد من التخصصات →",
    },
  };

  const isRTL = language === "ar";


const fetchSpecialites = async () => {
  setLoading(true);
  setError(null);

  try {
    const res = await fetch(`/api/specialties?locale=${language}`, { cache: "no-store" });
    if (!res.ok) throw new Error("Fetch failed");

    const json = await res.json();

    const mapped: Specialite[] = json.data
      .sort((a: any, b: any) => (a.Order ?? 0) - (b.Order ?? 0))
      .map((item: any) => {
        const description =
          item.Description?.map((block: any) =>
            block.children?.map((c: any) => c.text).join("")
          ).join(" ") || "";

        const coverUrl = item.cover?.formats?.large?.url
          ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${item.cover.formats.large.url}`
          : item.cover?.url
          ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${item.cover.url}`
          : "/images/default.jpg";

        return {
          id: item.id,
          title: item.Name || "",
          description,
                Slug: item.Slug,          // ✅ ADD THIS
          cover: coverUrl,
        };
      });

    setSpecialites(mapped);
  } catch (err) {
    console.error(err);
    setError("Failed loading specialties");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchSpecialites();
  }, [language]);


  // ---------- STATES ----------
  if (loading) {
    return (
      <div className="flex justify-center items-center py-24 text-gray-600 dark:text-gray-300">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-24 text-red-600">
        {error}
      </div>
    );
  }

  // ---------- UI ----------
  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`${theme === "dark" ? "bg-gray-900" : "bg-white"}`}
    >
{/* Header */}
<div className="w-full py-20">
  <div className="max-w-6xl mx-auto text-center px-4">
    <h2 className={`text-4xl md:text-5xl font-semibold ${isDark ? "text-white" : "text-black"}`}>
      {translations[language].title}
    </h2>
    <p className={`mt-6 text-lg md:text-xl max-w-3xl mx-auto ${isDark ? "text-white" : "text-black"}`}>
      {translations[language].subtitle}
    </p>
  </div>
</div>

      {/* Slider — style Dulux cards */}
      <div className="max-w-[1300px] mx-auto px-4 pb-20">
        <style>{`
          .dulux-card .arrow-btn {
            background: #ECECEC;
            color: #0863a9;
            transition: background .25s ease, color .25s ease;
          }
          .dulux-card:hover .arrow-btn,
          .dulux-card:focus-within .arrow-btn {
            background: #0863a9;
            color: #FFFFFF;
          }
          .dulux-card .arrow-btn svg {
            transition: transform .3s ease;
          }
          .dulux-card:hover .arrow-btn svg {
            transform: translateX(2px);
          }
          [dir="rtl"] .dulux-card:hover .arrow-btn svg {
            transform: translateX(-2px);
          }
          .dulux-card .title {
            transition: color .25s ease;
          }
          .dulux-card:hover .title {
            color: #0863a9;
          }
          /* External pagination styles */
          .external-pagination .swiper-pagination-bullet {
            background: #B9B9B9;
            opacity: .5;
            width: 8px;
            height: 8px;
          }
          .external-pagination .swiper-pagination-bullet-active {
            background: #0863a9;
            opacity: 1;
            width: 24px;
            border-radius: 4px;
          }
        `}</style>

        <Swiper
          modules={[Pagination]}
          pagination={{
            clickable: true,
            el: '.external-pagination', // �� point to external container
          }}
          spaceBetween={20}
          dir={isRTL ? "rtl" : "ltr"}
          breakpoints={{
            320: { slidesPerView: 1.15 },
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1280: { slidesPerView: 4 },
          }}
          className="dulux-swiper"
        >
          {specialites.map((sp) => (
            <SwiperSlide key={sp.id}>
              <Link
                href={`/Specialties/${sp.Slug}`}
                aria-label={sp.title}
                className={`dulux-card group relative flex flex-col h-full overflow-hidden ${
                  theme === "dark"
                    ? "bg-gray-800 border border-gray-700"
                    : "bg-white border border-gray-200"
                }`}
              >
                {/* Image */}
                <div className="relative w-full aspect-[16/9] overflow-hidden bg-gray-100 dark:bg-gray-900">
                  <img
                    src={sp.cover}
                    alt={sp.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                  />
                </div>

                {/* Body */}
                <div className="relative flex-1 flex flex-col px-5 pt-5 pb-16 min-h-[120px]">
                  <h3
                    className={`title text-[17px] font-medium leading-snug ${
                      theme === "dark" ? "text-gray-100" : "text-gray-700"
                    }`}
                  >
                    {sp.title}
                  </h3>

                  {/* Arrow button */}
                  <span
                    className="arrow-btn absolute bottom-0 right-0 w-12 h-12 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-5 h-5"
                    >
                      <path d="M5 12h14" />
                      <path d="M13 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* �� External pagination container – placed below the swiper */}
        <div className="external-pagination mt-8 flex justify-center"></div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link href="/Specialties">
            <button
              className={`px-6 py-3 font-semibold transition-colors ${
                theme === "dark"
                  ? "bg-blue-600 text-white hover:bg-blue-500"
                  : "bg-gray-900 text-white hover:bg-gray-800"
              }`}
            >
              {translations[language].buttoon}
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}